import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as SortAscending } from '../../../images/sort-ascending.svg';
import { ReactComponent as SortDescending } from '../../../images/sort-descending.svg';
import { ReactComponent as SwitchVertical } from '../../../images/switch-vertical.svg';
import SortDropdownOption from '../SortDropdownOption';
import './SortDropdown.scss';

export interface SortOption {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
}

export interface SortDropdownProps {
  options: SortOption[];
  selectedOption: string | number | null;
  direction: 'asc' | 'desc' | '';
  onOptionChange: (option: string | number | null) => void;
  onDirectionChange: (direction: 'asc' | 'desc' | '') => void;
  // isOpen: boolean;
  // onToggle: () => void;
  label?: string;
  placeholder?: string;
  className?: string;
  buttonLabel?: string;
  showCloseButton?: boolean;
  directionLabels?: {
    asc: string;
    desc: string;
  };
}

const SortDropdown: React.FC<SortDropdownProps> = ({
  options,
  selectedOption,
  direction,
  onOptionChange,
  onDirectionChange,
  // isOpen,
  // onToggle,
  label = 'Sort by',
  placeholder = 'Sort By',
  className = '',
  buttonLabel = 'Sort',
  showCloseButton = true,
  directionLabels = { asc: 'Ascending', desc: 'Descending' },
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [isOptionOpen, setIsOptionOpen] = useState(false);
  const [isDirectionOpen, setIsDirectionOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsOptionOpen(false);
        setIsDirectionOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsOptionOpen(false);
      setIsDirectionOpen(false);
    }
  }, [isOpen]);

  const handleOptionSelect = (value: string | number) => {
    onOptionChange(value);
    if (!direction) {
      onDirectionChange('asc');
    }
    setIsOptionOpen(false);
  };

  const handleDirectionSelect = (dir: string | number | 'asc' | 'desc') => {
    onDirectionChange(dir as 'asc' | 'desc');
    setIsDirectionOpen(false);
  };

  return (
    <div className={`sort-dropdown-wrapper-main ${className}`}>
      {/* Toggle Button */}
      <button className='sort-toggle-button' onClick={() => setIsOpen(!isOpen)} type='button' ref={buttonRef}>
        <SwitchVertical />
        <span>{buttonLabel}</span>
        <span className={isOpen ? 'fas fa-caret-up' : 'fas fa-caret-down'} />
      </button>

      {/* Sort Panel */}
      {isOpen && (
        <div className='sort-dropdown-container' ref={containerRef}>
          {label && <div className='sort-dropdown-label'>{label}</div>}
          <div className='sort-dropdown-row'>
            {/* Sort By Dropdown */}
            <div>
              <button
                className={`sort-dropdown-button ${isOptionOpen ? 'active' : ''}`}
                onClick={() => setIsOptionOpen(!isOptionOpen)}
                type='button'
              >
                <span className='sort-dropdown-button-text'>
                  {options.find((item: SortOption) => item.value === selectedOption)?.label || placeholder}
                </span>

                <span className={isOptionOpen ? 'fas fa-caret-up' : 'fas fa-caret-down'} />
              </button>

              <SortDropdownOption
                options={options}
                selectedValue={selectedOption ? selectedOption.toString() : ''}
                isOpen={isOptionOpen}
                onSelect={(dir) => handleOptionSelect(dir)}
                onClose={() => setIsOptionOpen(false)}
              />
            </div>

            {/* Direction Dropdown */}
            <div>
              <button
                className={`sort-dropdown-button sort-direction-button ${isDirectionOpen ? 'active' : ''}`}
                onClick={() => setIsDirectionOpen(!isDirectionOpen)}
                type='button'
              >
                {direction === 'asc' ? <SortAscending /> : <SortDescending />}
                <span className='sort-dropdown-button-text'>{direction ? directionLabels[direction] : 'Order'}</span>

                <span className={isDirectionOpen ? 'fas fa-caret-up' : 'fas fa-caret-down'} />
              </button>
              <SortDropdownOption
                options={[
                  { label: directionLabels.asc, value: 'asc', icon: <SortAscending /> },
                  { label: directionLabels.desc, value: 'desc', icon: <SortDescending /> },
                ]}
                selectedValue={direction}
                isOpen={isDirectionOpen}
                onSelect={(dir: string | number | 'asc' | 'desc') => handleDirectionSelect(dir)}
                onClose={() => setIsDirectionOpen(false)}
              />
            </div>

            {/* Clear Button */}
            {showCloseButton && (
              <button
                className='sort-dropdown-close'
                onClick={() => {
                  onOptionChange(null);
                  onDirectionChange('');
                }}
                aria-label='Clear sort selection'
              >
                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                  <path d='M1 1L13 13M1 13L13 1' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SortDropdown;
